import { useQuery } from '@tanstack/react-query';
import { App } from 'antd';
import { useEffect, useState } from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import { AUTH_SERVICES_BASE_URL, USERS_API_URL } from '@constants/url';
import useToken from '@hooks/useToken';
import useTopbar from '@hooks/useTopbar';
import { getFetch } from '@utils/get-fetch';
import { HttpError } from '@utils/request';

/**
 * A returned item either scopes a role to an object, or carries a role on its
 * own — a role the user holds outright rather than over something.
 */
type UserItem = {
	objectType?: string;
	objectId?: string;
	role: string;
};

/** An item scoped to an object, which is what the table lists. */
export type UserAssignment = Required<UserItem>;

const isAssignment = (item: UserItem): item is UserAssignment =>
	!!item.objectType && !!item.objectId;

type UsersResponse = {
	items: UserItem[];
	/** Cursor for the next page; null when there are no more. Unused for now. */
	nextCursor: string | null;
};

export const USERS_PAGE_SIZE = 50;

const useUsersPage = () => {
	const token = useToken();
	const { message } = App.useApp();

	// The searched client id, as opposed to what's currently typed.
	const [cid, setCid] = useState('');

	useTopbar(<Breadcrumbs />);

	const {
		data,
		isFetching,
		error,
	} = useQuery({
		queryKey: ['users', cid],
		queryFn: () =>
			getFetch<UsersResponse>(token, `${USERS_API_URL}/${encodeURIComponent(cid)}`, {
				pageSize: USERS_PAGE_SIZE,
			}),
		// Nothing to look up until a client id has been searched for.
		enabled: !!cid && !!token && !!AUTH_SERVICES_BASE_URL,
		// Don't retry auth failures (global default is retry: 3).
		retry: (failureCount, err) =>
			!(err instanceof HttpError && (err.status === 401 || err.status === 403)) &&
			failureCount < 3,
	});

	// useQuery has no onError in v5, so surface the failure here rather than
	// letting it show up as an empty table.
	useEffect(() => {
		if (error) {
			message.error(`Could not load users: ${error.message}`);
		}
	}, [error, message]);

	const search = (value: string) => {
		setCid(value.trim());
	};

	const items = data?.items ?? [];

	return {
		cid,
		search,
		items: items.filter(isAssignment),
		// Roles held outright are shown as tags instead of table rows. Deduped,
		// since the same role twice would just look like a bug.
		roles: [...new Set(items.filter((item) => !isAssignment(item)).map(({ role }) => role))],
		isFetching,
		hasSearched: !!cid,
	};
};

export default useUsersPage;
